import React from 'react';
import { APITypes } from '../../../primary-record-types';
import { FormStack } from '../../v2BaseComponents/Stack';
import { Field } from 'formik';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import {
    Validator,
    composeValidators,
    getDateFormatValidator,
    getMustComeAfterValidator,
    integer,
    minValueExclusive,
    mustComeBeforeValidator,
    numeric,
    required,
} from '../../../utils/validators';
import { getIntl } from '../../../utils/intl';
import { FormDirtyStateTracker } from '../FormExitValidation';
import { CenteredFormRow, SplitFormRow } from '../../v2BaseComponents/Form';
import { centsToDollarsConversion } from '../../../utils/centsToDollarsConversion';

const intl = getIntl('organization-client-billing-invoice-items-fields', {
    description: 'Description',
    periodStart: 'Period Start',
    periodEnd: 'Period End',
    requiredIfStartProvided: 'Required if start date is provided',
    requiredIfEndProvided: 'Required if end date is provided',
    fixed: 'Fixed Price - Service',
    unitBased: 'Variable Price - Hourly/Milage Rate',
    type: 'Charge Type',
    amount: 'Amount ($)',
    unitAmount: 'Unit Amount ($)',
    quantity: 'Quantity',
});

export type EditClientBillingInfoFieldsOwnProps = {
    values: FormValue;
};

export type EditClientBillingInfoFieldsProps = EditClientBillingInfoFieldsOwnProps;

type FixedAmountDetails = {
    amountType: 'fixed';
    amount: string;
};
type UnitBasedAmountDetails = {
    amountType: 'unit-based';
    unitAmount: string;
    quantity: string;
};

export type MergedAmountDetails = {
    amountType: 'fixed' | 'unit-based';
} & (FixedAmountDetails | UnitBasedAmountDetails);

export type InvoiceItemDetailsWithoutId = Omit<
    APITypes.organizationService.OrganizationInvoiceItemDetails,
    'invoiceItemId' | 'stripeCustomerId'
>;

export type FormValue = Omit<
    APITypes.organizationService.OrganizationInvoiceItemDetails,
    'invoiceItemId' | 'amountDetails' | 'stripeCustomerId'
> & {
    amountDetails: MergedAmountDetails;
};

export const getDefaultFormValue = (
    invoiceItem: APITypes.organizationService.GetOrganizationClientRecordInvoiceItemResponse,
): FormValue => {
    return {
        description: invoiceItem.description,
        period: {
            start: invoiceItem.period?.start || '',
            end: invoiceItem.period?.end || '',
        },
        amountDetails: {
            amountType: invoiceItem.amountDetails.amountType,
            amount:
                invoiceItem.amountDetails.amountType === 'fixed'
                    ? String(centsToDollarsConversion.convertCentsToDollars(invoiceItem.amountDetails.amount))
                    : '',
            unitAmount:
                invoiceItem.amountDetails.amountType === 'unit-based'
                    ? String(centsToDollarsConversion.convertCentsToDollars(invoiceItem.amountDetails.unitAmount))
                    : '',
            quantity:
                invoiceItem.amountDetails.amountType === 'unit-based' ? String(invoiceItem.amountDetails.quantity) : '',
        },
    };
};

export const newEntryFormValue: FormValue = {
    description: '',
    period: {
        start: '',
        end: '',
    },
    amountDetails: {
        amountType: 'fixed',
        amount: '',
    },
};

export const formValueToInvoiceItem = (formValue: FormValue): InvoiceItemDetailsWithoutId => {
    const base: Pick<InvoiceItemDetailsWithoutId, 'description' | 'period'> = {
        description: formValue.description,
        period: formValue.period?.start && formValue.period?.end ? formValue.period : undefined,
    };

    if (formValue.amountDetails.amountType === 'fixed') {
        return {
            ...base,
            amountDetails: {
                amountType: 'fixed',
                amount: centsToDollarsConversion.convertDollarsToCents(Number(formValue.amountDetails.amount)),
            },
        };
    } else {
        return {
            ...base,
            amountDetails: {
                amountType: 'unit-based',
                unitAmount: centsToDollarsConversion.convertDollarsToCents(Number(formValue.amountDetails.unitAmount)),
                quantity: Number(formValue.amountDetails.quantity),
            },
        };
    }
};

const EditClientBillingInfoFields: React.FC<EditClientBillingInfoFieldsProps> = ({ values }) => {
    const getPeriodValidate =
        (startOrEnd: 'start' | 'end'): Validator =>
        (value: unknown): string | undefined => {
            const otherValue = startOrEnd === 'start' ? values.period?.end : values.period?.start;
            if (otherValue && !value) {
                return intl(startOrEnd === 'start' ? 'requiredIfEndProvided' : 'requiredIfStartProvided');
            }
        };

    return (
        <FormStack>
            <CenteredFormRow>
                <Field
                    name="description"
                    title={intl('description')}
                    component={FormikInputs.Input}
                    validate={required}
                />
            </CenteredFormRow>
            <SplitFormRow>
                <Field
                    name="period.start"
                    title={intl('periodStart')}
                    component={FormikInputs.DateTimePicker}
                    validate={composeValidators(
                        getDateFormatValidator('MM/DD/YYYY'),
                        getPeriodValidate('start'),
                        mustComeBeforeValidator(values.period?.end || null),
                    )}
                />
                <Field
                    name="period.end"
                    title={intl('periodEnd')}
                    component={FormikInputs.DateTimePicker}
                    validate={composeValidators(
                        getDateFormatValidator('MM/DD/YYYY'),
                        getPeriodValidate('end'),
                        getMustComeAfterValidator(values.period?.start || null),
                    )}
                />
            </SplitFormRow>
            <CenteredFormRow>
                <Field
                    name="amountDetails.amountType"
                    title={intl('type')}
                    component={FormikInputs.Select}
                    validate={required}
                    selectOptions={[
                        { value: 'fixed', label: intl('fixed') },
                        { value: 'unit-based', label: intl('unitBased') },
                    ]}
                />
            </CenteredFormRow>
            {values.amountDetails.amountType === 'fixed' ? (
                <CenteredFormRow>
                    <Field
                        name="amountDetails.amount"
                        title={intl('amount')}
                        component={FormikInputs.Input}
                        validate={composeValidators(required, numeric, minValueExclusive(0))}
                    />
                </CenteredFormRow>
            ) : null}
            {values.amountDetails.amountType === 'unit-based' ? (
                <SplitFormRow>
                    <Field
                        name="amountDetails.quantity"
                        title={intl('quantity')}
                        component={FormikInputs.Input}
                        validate={composeValidators(required, minValueExclusive(0), integer)}
                    />
                    <Field
                        name="amountDetails.unitAmount"
                        title={intl('unitAmount')}
                        component={FormikInputs.Input}
                        validate={composeValidators(required, numeric, minValueExclusive(0))}
                    />
                </SplitFormRow>
            ) : null}
            <FormDirtyStateTracker />
        </FormStack>
    );
};

export default React.memo(EditClientBillingInfoFields);
